import { useState, useEffect } from 'react';
import { Grid, Users, ChevronsLeft, User, Clock, Award, Plus, AlertCircle, Loader, ChevronDown, ChevronUp, Timer, TrendingUp, Zap, List, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

const LightKeysCluster = ({ onBack }) => {
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [queueData, setQueueData] = useState([]);
    const [userPlaces, setUserPlaces] = useState([]);
    const [pendingPlaces, setPendingPlaces] = useState([]);
    const [fullPendingQueue, setFullPendingQueue] = useState([]);
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [showFullQueue, setShowFullQueue] = useState(false);
    const [statistics, setStatistics] = useState({
        total_in_queue: 0,
        user_places: 0,
        completed_places: 0,
        earned: 0,
        bank_clones: 0,
        estimated_time_to_payment: 'Загрузка...'
    });
    const navigate = useNavigate();

    const [canPurchase, setCanPurchase] = useState(true);
    const [purchaseInfo, setPurchaseInfo] = useState(null);

    // STATE ДЛЯ ТАЙМЕРА И СТАТУСОВ
    const [purchaseCountdown, setPurchaseCountdown] = useState(0);
    const [nextPurchaseTime, setNextPurchaseTime] = useState(null);
    const [purchaseStatus, setPurchaseStatus] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // ✅ НОВОЕ: Состояние для ручной проверки
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [processingTime, setProcessingTime] = useState(0);
    const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: '' }

    // Форматирование даты с микросекундами
    const formatDateWithMicroseconds = (dateString) => {
        if (!dateString) return 'Н/Д';
        if (dateString.includes('.') && dateString.split('.').length === 4) {
            return dateString;
        }
        const parts = dateString.split(' ');
        if (parts.length === 2) {
            const [datePart, timePart] = parts;
            const [year, month, day] = datePart.split('-');
            const [time, microseconds] = timePart.split('.');
            return `${day}.${month}.${year} ${time}.${microseconds || '000000'}`;
        }
        return dateString;
    };

    const MicrosecondsDisplay = ({ dateString, short = false }) => {
        const formatted = formatDateWithMicroseconds(dateString);
        const parts = formatted.split('.');

        if (parts.length === 4) {
            const microseconds = parts[3];
            if (short) {
                return (
                    <div className="text-[10px] leading-tight">
                        <div className="font-mono text-gray-600">{parts[0]}.{parts[1]}.{parts[2]}</div>
                        <div className="font-mono text-purple-500 font-semibold">{parts[3].substring(0, 2)}:{parts[3].substring(2, 4)} .{microseconds}</div>
                    </div>
                );
            }
            return (
                <span className="font-mono text-xs sm:text-sm">
                    {parts[0]}.{parts[1]}.{parts[2]} {parts[3].substring(0, 2)}:{parts[3].substring(2, 4)}:{parts[3].substring(4, 6)}<span className="text-purple-500 font-semibold">.{microseconds}</span>
                </span>
            );
        }
        return <span className="font-mono text-xs sm:text-sm">{formatted}</span>;
    };

    // Форматирование таймера
    const formatCountdown = (seconds) => {
        if (seconds <= 0) return { days: 0, hours: '00', mins: '00', secs: '00' };

        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return {
            days,
            hours: hours.toString().padStart(2, '0'),
            mins: mins.toString().padStart(2, '0'),
            secs: secs.toString().padStart(2, '0')
        };
    };

    const countdown = formatCountdown(purchaseCountdown);

    // ✅ Таймер времени обработки покупки
    useEffect(() => {
        if (!isProcessing || !purchaseStatus?.clicked_at) {
            setProcessingTime(0);
            return;
        }

        const startTime = new Date(purchaseStatus.clicked_at).getTime();

        const timer = setInterval(() => {
            const now = Date.now();
            const elapsed = Math.floor((now - startTime) / 1000);
            setProcessingTime(elapsed);
        }, 1000);

        return () => clearInterval(timer);
    }, [isProcessing, purchaseStatus]);
    useEffect(() => {
        if (purchaseCountdown <= 0) {
            setCanPurchase(true);
            setNextPurchaseTime(null);
            return;
        }

        const timer = setInterval(() => {
            setPurchaseCountdown(prev => {
                const newValue = Math.max(0, prev - 1);

                if (newValue <= 0) {
                    setCanPurchase(true);
                    setNextPurchaseTime(null);
                    // Автоматическая проверка только когда таймер достигает 0
                    setTimeout(() => checkPurchaseAvailability(), 500);
                }

                return newValue;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [purchaseCountdown]);

    // ✅ УБРАНА автоматическая проверка статуса каждые 3 секунды!
    // Теперь проверка происходит только вручную или при загрузке страницы

    // ✅ РУЧНАЯ проверка статуса покупки (вызывается кнопкой)
    const checkPurchaseStatus = async (showNotification = false) => {
        setIsCheckingStatus(true);
        try {
            const response = await axios.get(
                'https://api.lite-com.ru/api/v1/light-keys/purchase-status',
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                }
            );

            if (response.data.success && response.data.data.has_click) {
                const data = response.data.data;

                console.log('📋 [PURCHASE STATUS]', data);

                if (data.status === 'pending') {
                    setPurchaseStatus(data);
                    setIsProcessing(true);
                }

                if (data.status === 'failed') {
                    setIsProcessing(false);
                    setPurchaseStatus(data);
                    setNotification({
                        type: 'error',
                        message: data.error_message || 'Ошибка обработки покупки'
                    });
                    setTimeout(() => setNotification(null), 5000);
                }
            } else {
                // Клика нет - значит обработан успешно
                if (isProcessing) {
                    await loadData();
                    await checkPurchaseAvailability();

                    setNotification({
                        type: 'success',
                        message: 'Покупка успешно обработана! Место добавлено в очередь.'
                    });
                    setTimeout(() => setNotification(null), 5000);

                    setIsProcessing(false);
                }

                setPurchaseStatus(null);
            }
        } catch (error) {
            console.error('Ошибка проверки статуса покупки:', error);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    // ✅ Проверка возможности покупки
    const checkPurchaseAvailability = async () => {
        try {
            const response = await axios.get(
                'https://api.lite-com.ru/api/v1/light-keys/can-purchase',
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                }
            );

            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setPurchaseInfo(data);
                setCanPurchase(data.can_purchase);

                const secondsRemaining = data.seconds_until_available || 0;
                setPurchaseCountdown(secondsRemaining);

                if (data.next_available_at) {
                    setNextPurchaseTime(data.next_available_at);
                } else {
                    setNextPurchaseTime(null);
                }

                console.log('🔍 [PURCHASE CHECK]', {
                    can_purchase: data.can_purchase,
                    reason: data.reason,
                    seconds_until_available: secondsRemaining,
                    next_available_at: data.next_available_at
                });
            }
        } catch (error) {
            console.error('Ошибка проверки возможности покупки:', error);
        }
    };

    const toggleCard = (id) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedCards(newExpanded);
    };

    // ✅ Загрузка данных ТОЛЬКО при монтировании (БЕЗ автоматических обновлений)
    useEffect(() => {
        loadData();
        checkPurchaseAvailability();

        // ✅ НОВОЕ: Проверяем, есть ли необработанная покупка (ОДИН РАЗ при загрузке)
        // Передаем true, чтобы показать уведомление пользователю
        setTimeout(() => {
            checkPurchaseStatus(true);
        }, 1000); // Задержка 1 сек, чтобы страница успела загрузиться

        // ❌ УБРАНО автоматическое обновление каждые 30 секунд
        // Теперь данные обновляются ТОЛЬКО вручную или после успешной покупки
    }, []);

    const loadData = async () => {
        try {
            const response = await axios.get(
                'https://api.lite-com.ru/api/v1/light-keys/queue-data',
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                }
            );

            if (response.data.success) {
                const { queue, user_places, pending_places, statistics: stats, full_pending_queue } = response.data.data;

                const userPlacesData = user_places || [];
                const pendingPlacesData = pending_places || [];
                const fullPendingQueueData = full_pending_queue || [];

                setQueueData(queue);
                setUserPlaces(userPlacesData);
                setPendingPlaces(pendingPlacesData);
                setFullPendingQueue(fullPendingQueueData);
                setStatistics(stats);
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки данных:', error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Обработка покупки
    const handlePurchase = async () => {
        if (purchasing || !canPurchase || isProcessing || purchaseCountdown > 0) return;

        setPurchasing(true);

        try {
            const response = await axios.post(
                'https://api.lite-com.ru/api/v1/light-keys/purchase',
                {},
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                }
            );

            if (response.data.success) {
                setPurchaseStatus({
                    ...response.data.data,
                    status: 'pending'
                });

                setIsProcessing(true);

                alert('🎉 Покупка зарегистрирована! Нажмите кнопку "Проверить статус", чтобы узнать результат обработки.');

                // Обновляем доступность покупки
                await checkPurchaseAvailability();
            } else {
                alert('❌ ' + (response.data.message || 'Ошибка при покупке'));
            }
        } catch (error) {
            console.error('Ошибка покупки:', error);
            const errorMessage = error.response?.data?.message || 'Ошибка при регистрации покупки';

            if (error.response?.status === 429) {
                alert(`⏰ ${errorMessage}`);
            } else {
                alert('❌ ' + errorMessage);
            }
        } finally {
            setPurchasing(false);
        }
    };

    const handleBack = () => {
        navigate('/dashboard/clusters');
    };

    const getQueueStatusClass = (status, isCurrentUser = false) => {
        if (isCurrentUser) return 'bg-blue-100 border-blue-300 shadow-md';
        switch (status) {
            case 'completed': return 'bg-green-100 border-green-300';
            case 'next-to-pay': return 'bg-yellow-100 border-yellow-300';
            case 'waiting-payment': return 'bg-orange-100 border-orange-300';
            case 'active': return 'bg-gray-50 border-gray-200';
            default: return 'bg-gray-50 border-gray-200';
        }
    };

    // Компоненты карточек
    const FullQueueCard = ({ place }) => {
        const isCurrentUser = place.isCurrentUser || false;

        return (
            <div className={`flex items-center justify-between p-2.5 rounded-lg border ${
                isCurrentUser
                    ? 'bg-blue-50 border-blue-300 shadow-sm'
                    : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center space-x-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        isCurrentUser
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700'
                    }`}>
                        {place.queue_position}
                    </div>

                    <div>
                        <div className={`text-xs font-semibold ${
                            isCurrentUser ? 'text-blue-700' : 'text-gray-800'
                        }`}>
                            {place.login}
                            {isCurrentUser && (
                                <span className="ml-1.5 px-1.5 py-0.5 bg-blue-500 text-white text-[8px] rounded font-bold">
                                    ВЫ
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] text-gray-500 flex items-center">
                            <Clock size={10} className="mr-1" />
                            {place.waiting_time}
                        </div>
                    </div>
                </div>

                <div className="text-right">
                    <MicrosecondsDisplay dateString={place.created_at} short={true} />
                </div>
            </div>
        );
    };

    const PendingPlaceCard = ({ place }) => {
        const isExpanded = expandedCards.has(`pending-${place.id}`);
        return (
            <div className="bg-white border-2 border-orange-300 rounded-lg p-3 shadow-md">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                        <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center shadow-sm">
                            <span className="text-white font-bold text-sm">#{place.queue_position}</span>
                        </div>
                        <div>
                            <div className="text-sm font-bold text-gray-800">Позиция {place.queue_position}</div>
                            <div className="text-xs text-orange-600 flex items-center">
                                <Clock size={12} className="mr-1" />
                                <MicrosecondsDisplay dateString={place.created_at} short={true} />
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-base font-bold text-gray-900">${place.amount}</div>
                        <span className="px-2 py-1 inline-flex text-xs font-bold rounded-full bg-orange-100 text-orange-700">
                            В ожидании
                        </span>
                    </div>
                </div>
                <button
                    onClick={() => toggleCard(`pending-${place.id}`)}
                    className="w-full flex items-center justify-between text-xs text-orange-600 hover:text-orange-700 mt-2 pt-2 border-t border-orange-200 transition-colors"
                >
                    <span className="font-medium">Показать детали</span>
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {isExpanded && (
                    <div className="mt-2 pt-2 border-t border-orange-200 space-y-2">
                        <div>
                            <div className="text-xs text-gray-500 mb-1">Дата покупки:</div>
                            <MicrosecondsDisplay dateString={place.purchase_date} short={true} />
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">ID:</div>
                            <div className="text-xs font-mono text-gray-700">{place.id}</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500 mb-1">Уровень:</div>
                            <div className="text-xs font-medium text-gray-700">{place.level}</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const UserPlaceCard = ({ place }) => {
        const isExpanded = expandedCards.has(`user-${place.id}`);
        return (
            <div className="bg-white border border-purple-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-purple-600 font-bold text-sm">#{place.queue_position}</span>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-gray-800">Позиция {place.queue_position}</div>
                            <div className="text-[10px] text-purple-600 flex items-center">
                                <TrendingUp size={10} className="mr-1" />
                                {place.positions_to_complete} до завершения
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-bold text-gray-900">${place.amount}</div>
                        <span className="px-2 py-0.5 inline-flex text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700">
                            {place.status}
                        </span>
                    </div>
                </div>
                <button
                    onClick={() => toggleCard(`user-${place.id}`)}
                    className="w-full flex items-center justify-between text-xs text-purple-600 hover:text-purple-700 mt-2 pt-2 border-t border-purple-100 transition-colors"
                >
                    <span className="font-medium">Показать детали</span>
                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {isExpanded && (
                    <div className="mt-2 pt-2 border-t border-purple-100 space-y-1">
                        <div>
                            <div className="text-[10px] text-gray-500 mb-0.5">Дата покупки:</div>
                            <MicrosecondsDisplay dateString={place.purchase_date} short={true} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ✅ Определяем можно ли нажать кнопку
    const isPurchaseDisabled = purchasing || !canPurchase || isProcessing || purchaseCountdown > 0;

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-3 border-gray-200 border-t-purple-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600 font-medium">Загрузка данных...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Уведомления */}
            {notification && (
                <div
                    className={`fixed top-4 right-4 z-50 max-w-md transition-all duration-300 ease-in-out ${
                        notification.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
                    } border-2 rounded-lg p-4 shadow-lg`}
                    style={{ animation: 'slideInRight 0.3s ease-out' }}
                >
                    <div className="flex items-start gap-3">
                        {notification.type === 'success' ? (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        ) : (
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        )}
                        <div className="flex-1">
                            <p className={`text-sm font-semibold ${
                                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                            }`}>
                                {notification.message}
                            </p>
                        </div>
                        <button
                            onClick={() => setNotification(null)}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
            `}</style>

            {/* Заголовок */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm w-full">
                <div className="px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <button
                                className="mr-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                onClick={handleBack}
                            >
                                <ChevronsLeft size={20} className="text-gray-600" />
                            </button>
                            <div className="flex items-center">
                                <div className="w-9 h-9 rounded-lg bg-purple-500 flex items-center justify-center shadow-sm mr-2">
                                    <Grid size={18} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-800">Light Keys</h3>
                                    <p className="text-[10px] text-gray-500">Матричная система</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 gap-2">
                        <div className="flex items-center bg-purple-50 px-2.5 py-1.5 rounded-lg flex-1">
                            <span className="text-[10px] text-gray-600 mr-1.5">Место:</span>
                            <span className="text-xs font-bold text-purple-600">$50</span>
                        </div>
                        <div className="flex items-center bg-green-50 px-2.5 py-1.5 rounded-lg flex-1">
                            <span className="text-[10px] text-gray-600 mr-1.5">Выплата:</span>
                            <span className="text-xs font-bold text-green-600">$120</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Основной контент */}
            <div className="pb-6">
                {/* Статистика */}
                <div className="px-4 pt-3 w-full">
                    <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center mb-1">
                                <Users size={12} className="mr-1.5 text-purple-500" />
                                <span className="text-[10px] font-medium text-gray-600">Всего</span>
                            </div>
                            <p className="text-xl font-bold text-purple-600">{statistics.total_in_queue}</p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center mb-1">
                                <User size={12} className="mr-1.5 text-blue-500" />
                                <span className="text-[10px] font-medium text-gray-600">Ваши</span>
                            </div>
                            <p className="text-xl font-bold text-blue-600">{statistics.user_places}</p>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center mb-1">
                                <Award size={12} className="mr-1.5 text-green-500" />
                                <span className="text-[10px] font-medium text-gray-600">Заработано</span>
                            </div>
                            <p className="text-xl font-bold text-green-600">${statistics.earned}</p>
                        </div>

                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 shadow-sm">
                            <div className="flex items-center mb-1">
                                <Clock size={12} className="mr-1.5 text-orange-500" />
                                <span className="text-[10px] font-medium text-gray-600">До выплаты</span>
                            </div>
                            <p className="text-[10px] font-bold text-orange-600 leading-tight">{statistics.estimated_time_to_payment}</p>
                        </div>
                    </div>

                    {/* Банк клонов */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3 mb-3 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <h4 className="text-sm font-bold text-gray-800 flex items-center mb-0.5">
                                    <Zap size={14} className="mr-1.5 text-purple-500" />
                                    Банк Клонов
                                </h4>
                                <p className="text-[10px] text-gray-600">Общий баланс</p>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-bold text-purple-600">${statistics.bank_clones}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Очередь выплат */}
                <div className="mb-3">
                    <div className="px-4 mb-2">
                        <h4 className="text-sm font-bold text-gray-800 flex items-center">
                            <div className="w-1 h-4 bg-purple-500 rounded mr-2"></div>
                            Очередь выплат
                        </h4>
                    </div>

                    <div className="overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <div className="flex space-x-2 pl-4 pr-4">
                            {queueData.map((item, index) => (
                                <div
                                    key={index}
                                    className={`relative flex-shrink-0 w-14 h-14 rounded-lg border-2 ${getQueueStatusClass(item.status, item.isCurrentUser)} flex flex-col items-center justify-center p-1 transition-all ${
                                        item.isCurrentUser ? 'scale-110 ring-2 ring-blue-400' : ''
                                    }`}
                                >
                                    {item.login ? (
                                        <>
                                            <div className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-700 font-bold text-xs">
                                                {item.login.charAt(0).toUpperCase()}
                                            </div>
                                            <p className="text-[8px] font-medium text-gray-700 mt-0.5 truncate w-full text-center">
                                                {item.login.substring(0, 5)}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                                                <Plus size={10} className="text-gray-400" />
                                            </div>
                                            <p className="text-[8px] font-medium text-gray-400 mt-0.5">Свободно</p>
                                        </>
                                    )}
                                    {item.isCurrentUser && (
                                        <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-500 shadow"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="px-4">
                        <div className="h-1.5 bg-gray-200 w-full rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: '35%' }}></div>
                        </div>
                    </div>
                </div>


                {/* Ожидают расстановки - ВАШИ места */}
                {pendingPlaces && pendingPlaces.length > 0 && (
                    <div className="mb-3 px-4">
                        <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 mb-2">
                            <h4 className="text-sm font-bold text-gray-800 flex items-center">
                                <Loader className="animate-spin mr-2 text-orange-500" size={16} />
                                Ваши места в ожидании ({pendingPlaces.length})
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                                Места будут размещены в структуре автоматически
                            </p>
                        </div>
                        <div className="space-y-2">
                            {pendingPlaces.map((place, index) => (
                                <PendingPlaceCard key={`pending-${place.id}-${index}`} place={place} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Полная предварительная очередь */}
                {fullPendingQueue && fullPendingQueue.length > 0 && (
                    <div className="mb-3 px-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                            <button
                                onClick={() => setShowFullQueue(!showFullQueue)}
                                className="w-full flex items-center justify-between text-left"
                            >
                                <div className="flex items-center">
                                    <List size={16} className="mr-2 text-purple-500" />
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-800">
                                            Полная предварительная очередь
                                        </h4>
                                        <p className="text-xs text-gray-500">
                                            Всего участников: {fullPendingQueue.length}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <span className="text-xs text-purple-600 font-medium mr-2">
                                        {showFullQueue ? 'Скрыть' : 'Показать'}
                                    </span>
                                    {showFullQueue ? (
                                        <ChevronUp size={16} className="text-purple-600" />
                                    ) : (
                                        <ChevronDown size={16} className="text-purple-600" />
                                    )}
                                </div>
                            </button>

                            {showFullQueue && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="space-y-1.5 max-h-96 overflow-y-auto">
                                        {fullPendingQueue.map((place, index) => (
                                            <FullQueueCard
                                                key={`full-queue-${place.id}-${index}`}
                                                place={place}
                                            />
                                        ))}
                                    </div>

                                    <div className="mt-3 pt-3 border-t border-gray-100 text-center">
                                        <div className="flex justify-around text-xs">
                                            <div>
                                                <div className="text-gray-500">В очереди</div>
                                                <div className="font-bold text-purple-600">
                                                    {fullPendingQueue.length}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-gray-500">Ваших мест</div>
                                                <div className="font-bold text-blue-600">
                                                    {fullPendingQueue.filter(p => p.isCurrentUser).length}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Ваши места в структуре */}
                <div className="mb-3 px-4">
                    <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center">
                        <div className="w-1 h-4 bg-purple-500 rounded mr-2"></div>
                        Ваши места в структуре
                    </h4>
                    {userPlaces.length > 0 ? (
                        <div className="space-y-2">
                            {userPlaces.map((place, index) => (
                                <UserPlaceCard key={index} place={place} />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-gray-200">
                            <User className="mx-auto text-gray-300 mb-2" size={32} />
                            <p className="text-sm text-gray-500 font-medium">У вас пока нет мест в очереди</p>
                            <p className="text-xs text-gray-400 mt-1">Купите первое место, чтобы начать!</p>
                        </div>
                    )}
                </div>

                {/* Информация */}
                <div className="px-4 mb-4">
                    <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                        <div className="flex items-start">
                            <AlertCircle size={16} className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                                <h4 className="text-xs font-bold text-purple-700 mb-1">Как работает Light Keys?</h4>
                                <p className="text-[10px] text-gray-600 leading-relaxed mb-1">
                                    Покупая место за $50, вы получаете выплаты:
                                </p>
                                <ul className="text-[10px] text-gray-600 space-y-0.5">
                                    <li>• <strong>1 место:</strong> $15 реферальных</li>
                                    <li>• <strong>2 место:</strong> $15 в банк клонов</li>
                                    <li>• <strong>3 место:</strong> $120 на вывод</li>
                                    <li>• <strong>4 место:</strong> $50 реинвест</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ✅ БЛОК ПОКУПКИ С ТАЙМЕРОМ */}
                <div className="px-4 mb-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">

                        {/* ✅ Индикатор обработки */}
                        {isProcessing && purchaseStatus && (
                            <div className="mb-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg p-4 animate-pulse">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center">
                                        <Loader className="animate-spin mr-2 text-blue-500" size={20} />
                                        <span className="font-semibold text-blue-700 text-base">🔄 Покупка обрабатывается...</span>
                                    </div>
                                    <Timer size={18} className="text-blue-500" />
                                </div>
                                <div className="bg-white/50 rounded-lg p-3 mb-3">
                                    <p className="text-sm text-gray-700 mb-2">
                                        <span className="font-semibold">Клик зарегистрирован:</span>
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center justify-between">
                                            <MicrosecondsDisplay dateString={purchaseStatus.clicked_at} short={false} />
                                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                                {purchaseStatus.status || 'pending'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded flex items-center justify-between">
                                    <span>
                                        ⏱️ Обработка обычно занимает 1-3 минуты.
                                    </span>
                                    {processingTime > 0 && (
                                        <span className="font-mono font-semibold text-blue-600">
                                            {Math.floor(processingTime / 60)}:{(processingTime % 60).toString().padStart(2, '0')}
                                        </span>
                                    )}
                                </div>
                                {purchaseStatus.error_message && (
                                    <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                                        ❌ {purchaseStatus.error_message}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ✅ Таймер кулдауна */}
                        {purchaseCountdown > 0 && (
                            <div className="mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-300 rounded-lg p-3">
                                <div className="flex items-center mb-2">
                                    <Clock className="mr-2 text-orange-500" size={18} />
                                    <span className="font-semibold text-orange-700 text-sm">Следующая покупка доступна через:</span>
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    {countdown.days > 0 && (
                                        <>
                                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                                <div className="text-xl font-bold text-orange-600">{countdown.days}</div>
                                                <div className="text-[10px] text-gray-500">дней</div>
                                            </div>
                                            <span className="text-orange-500 font-bold">:</span>
                                        </>
                                    )}
                                    <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                        <div className="text-xl font-bold text-orange-600">{countdown.hours}</div>
                                        <div className="text-[10px] text-gray-500">часов</div>
                                    </div>
                                    <span className="text-orange-500 font-bold">:</span>
                                    <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                        <div className="text-xl font-bold text-orange-600">{countdown.mins}</div>
                                        <div className="text-[10px] text-gray-500">минут</div>
                                    </div>
                                    <span className="text-orange-500 font-bold">:</span>
                                    <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                        <div className="text-xl font-bold text-orange-600">{countdown.secs}</div>
                                        <div className="text-[10px] text-gray-500">секунд</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ✅ Кнопки действий */}
                        <div className="flex gap-2">
                            {/* Кнопка покупки */}
                            <button
                                onClick={handlePurchase}
                                disabled={isPurchaseDisabled}
                                className={`flex-1 py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center transition-all shadow-md ${
                                    isPurchaseDisabled
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-xl hover:scale-105'
                                }`}
                            >
                                {purchasing ? (
                                    <>
                                        <Loader className="animate-spin mr-2" size={16} />
                                        Отправка запроса...
                                    </>
                                ) : isProcessing ? (
                                    <>
                                        <Loader className="animate-spin mr-2" size={16} />
                                        Обработка покупки...
                                    </>
                                ) : purchaseCountdown > 0 ? (
                                    <>
                                        <Clock className="mr-2" size={16} />
                                        Покупка недоступна
                                    </>
                                ) : !canPurchase ? (
                                    <>
                                        <Clock className="mr-2" size={16} />
                                        Покупка недоступна
                                    </>
                                ) : (
                                    <>
                                        <Zap className="mr-2" size={16} />
                                        Купить место за $50
                                    </>
                                )}
                            </button>

                            {/* ✅ НОВАЯ КНОПКА: Проверить статус вручную */}
                            {isProcessing && (
                                <button
                                    onClick={checkPurchaseStatus}
                                    disabled={isCheckingStatus}
                                    className="py-3 px-4 rounded-xl font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-all flex items-center gap-2 disabled:opacity-50 shadow-md hover:shadow-xl"
                                >
                                    {isCheckingStatus ? (
                                        <>
                                            <Loader className="animate-spin" size={16} />
                                            <span className="hidden sm:inline">Проверка...</span>
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw size={16} />
                                            <span className="hidden sm:inline">Проверить</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Подсказка */}
                        {canPurchase && !purchasing && !isProcessing && purchaseCountdown === 0 && (
                            <p className="text-center text-[10px] text-gray-500 mt-2">
                                После покупки следующая будет доступна через 24 часа
                            </p>
                        )}

                        {isProcessing && (
                            <p className="text-center text-xs text-blue-600 mt-3 font-medium bg-blue-50 py-2 px-4 rounded-lg border border-blue-200">
                                ⚡ Ваша покупка в обработке. Нажмите "Проверить статус" для обновления.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LightKeysCluster;
